import { useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import HoverPopover from './HoverPopover';

/**
 * Reusable modal component matching the prototype's modal design language.
 *
 * Props:
 *   open       – boolean
 *   onClose    – () => void
 *   title      – string | ReactNode
 *   size       – 'default' | 'wide' | 'xl' (default: 'default')
 *   footer     – ReactNode (optional)
 *   children   – ReactNode
 */
export default function Modal({
  open,
  onClose,
  title,
  size = 'default',
  footer,
  children,
  headerActions,
  bodyClassName = '',
  footerClassName = '',
  containerClassName = '',
}) {
  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const widthMap = {
    default: 'w-[560px]',
    wide:    'w-[740px]',
    xl:      'w-[min(920px,96vw)]',
  };

  return (
    // No backdrop-filter blur on the overlay: it forces the compositor to
    // re-rasterize the whole blurred region while the modal body scrolls — the
    // draft modals (big contentEditable pages) turned visibly laggy. Same fix
    // as the AttachDraftPicker; a slightly darker scrim keeps the separation.
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,17,31,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`relative bg-white rounded-2xl ${widthMap[size] || widthMap.default} max-w-[95vw] max-h-[88vh] overflow-hidden flex flex-col ${containerClassName}`}
        style={{
          borderTop: '3px solid #145f5f',
          border: '1px solid rgba(8, 17, 31, 0.08)',
          boxShadow: '0 10px 30px -10px rgba(8, 17, 31, 0.12), 0 30px 70px -15px rgba(8, 17, 31, 0.22), inset 0 1px 0 rgba(255,255,255,0.6)'
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-20 flex items-center justify-between px-6 py-4 flex-shrink-0 bg-[#faf9f6]"
          style={{ borderBottom: '1.5px solid #e2d9c8' }}
        >
          <div
            className="text-[18px] font-bold text-gray-900"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {title}
          </div>
          <div className="ml-3 flex items-center gap-1.5">
            {headerActions}
            <HoverPopover variant="tooltip" content="Close (Esc)">
              <button
                onClick={onClose}
                aria-label="Close"
                className="w-8 h-8 rounded-[10px] border-[1.5px] border-[#ddd6c6] bg-white flex items-center justify-center text-[#7a7060] transition-all duration-200 hover:bg-[#faf2f2] hover:border-[#f3d7d7] hover:text-[#9a564a] active:scale-95"
              >
                <FiX size={17} />
              </button>
            </HoverPopover>
          </div>
        </div>

        {/* Body */}
        <div className={`flex-1 overflow-y-auto ${bodyClassName}`}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className={`sticky bottom-0 z-20 flex items-center justify-end gap-2.5 px-6 py-4 flex-shrink-0 bg-[#faf9f6] ${footerClassName}`}
            style={{ borderTop: '1.5px solid #e2d9c8' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
